'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';
import PreCartQuestionnaire from './PreCartQuestionnaire';
import { trackAddToCart } from '@/lib/tracking/events';
import { useAuth } from '../../../contexts/AuthContext';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { clearPendingEntry, loadPendingEntry, savePendingEntry } from '@/lib/pendingEntry';
import { pricePerTicketFor, visibleTiers, resolveTierPrice, tierDiscountPercent } from '@/lib/pricing';


const TicketSelector = ({
  competition,
  selectedNumbers,
  reservedNumbers,
  availableNumbers = [], // NEW: Receive pre-calculated available numbers
  userPurchasedNumbers = [], // For chance-of-winning calc — counts toward the user's total entries
  isReservationMinimised,
  onQuantityChange,
  onPromoCodeApply,
  onAgeVerificationChange
}) => {
  // CRITICAL FIX: Force lucky-dip mode for instant win competitions
  // Check database column instant_win_type for value 'instant_win'
  const isInstantWin =
  competition?.instant_win_type === 'instant_win' ||
  competition?.instantWinType === 'instant_win';
  const [selectionMode, setSelectionMode] = useState(isInstantWin ? 'lucky-dip' : 'manual');
  // The lucky dip opens on 10 tickets (Rob), or on every ticket left when
  // fewer than 10 are.
  const [quantity, setQuantity] = useState(() => {
    const left = Number(competition?.totalTickets || 0) - Number(competition?.ticketsSold || 0);
    return left > 0 ? Math.max(1, Math.min(10, left)) : 10;
  });
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState(null);
  // Set when the server says the answer is wrong.
  const [answerError, setAnswerError] = useState('');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showSelectionConfirmation, setShowSelectionConfirmation] = useState(false);
  const [cartAlert, setCartAlert] = useState(null); // NEW: In-app alert state
  // MOBILE ONLY: control floating CTA visibility
  const [showMobileCTA, setShowMobileCTA] = useState(false);
  const observerRef = useRef(null);


  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const pathname = usePathname();
  // Set when a visitor who started entering before logging in comes back.
  const [resumed, setResumed] = useState(null);

  // DEBUGGING: Log instant win detection in TicketSelector
  useEffect(() => {
    console.log('🎰 TicketSelector - Instant Win Check:', {
      competitionId: competition?.id,
      instant_win_type: competition?.instant_win_type,
      isInstantWin: isInstantWin,
      initialMode: isInstantWin ? 'lucky-dip' : 'manual',
      currentMode: selectionMode
    });
  }, [competition, isInstantWin, selectionMode]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    })?.format(amount);
  };

  const handleQuantityChange = (newQuantity) => {
    const validQuantity = Math.max(1, Math.min(newQuantity, 50));
    setQuantity(validQuantity);
    onQuantityChange(validQuantity);
  };

  // A visitor who chose tickets before logging in: restore their choice.
  useEffect(() => {
    if (!isAuthenticated || !competition?.id) return;
    const pending = loadPendingEntry();
    if (!pending || pending.competitionId !== competition.id) return;
    if (!isInstantWin && pending.mode) setSelectionMode(pending.mode);
    if (pending.quantity > 0) handleQuantityChange(pending.quantity);
    setResumed(pending);
    // Restore once, when the player is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, competition?.id]);

  // An answer has been chosen. It's checked on the server when the tickets
  // go into the basket.
  const handleQuestionnaireComplete = (answers) => {
    setQuestionnaireAnswers(answers);
    setQuestionnaireCompleted(Boolean(answers?.general_knowledge));
    setAnswerError('');
  };

  // CRITICAL: Prevent mode switching for instant win competitions
  useEffect(() => {
    if (isInstantWin) {
      setSelectionMode('lucky-dip');
    }
  }, [isInstantWin]);

  // Enhanced effect to show persistent confirmation when selections exist
  useEffect(() => {
    if (reservedNumbers?.length > 0) {
      setShowSelectionConfirmation(true);
    } else {
      setShowSelectionConfirmation(false);
    }
  }, [reservedNumbers]);

  // NEW: Auto-clear cart alerts after 5 seconds
  useEffect(() => {
    if (cartAlert) {
      const timer = setTimeout(() => {
        setCartAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [cartAlert]);

  useEffect(() => {
  // Mobile only
  if (typeof window === 'undefined') return;
  if (window.innerWidth >= 1024) return; // lg and up = ignore

  // The page's opening section (title, price and countdown). Once it has
  // scrolled out of view, keep a "Pick your numbers" button on screen.
  const target = document.getElementById('comp-title')?.closest('section');

  // No opening section: show the button straight away.
  if (!target || typeof IntersectionObserver === 'undefined') {
    setShowMobileCTA(true);
    return;
  }

  observerRef.current = new IntersectionObserver(
    ([entry]) => {
      setShowMobileCTA(!entry.isIntersecting);
    },
    {
      root: null,
      threshold: 0
    }
  );

  observerRef?.current?.observe(target);

  return () => {
    if (observerRef?.current) {
      observerRef?.current?.disconnect();
      observerRef.current = null;
    }
  };
}, []);

  // Open the number grid and bring it into view (on phones it sits above
  // this panel, on larger screens beside it).
  const openNumberPicker = () => {
    if (window.setActiveTab) {
      window.setActiveTab('numbers');
    } else {
      window.dispatchEvent(new CustomEvent('selectNumbersClick'));
    }
    setTimeout(() => {
      document.getElementById('pick-numbers')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };


  const handleAddToCart = async () => {
    // CRITICAL VALIDATION: Ensure all requirements are met
    if (!questionnaireCompleted) {
      setCartAlert({
        type: 'error',
        message: '⚠️ Please answer the question first'
      });
      return;
    }
    
    if (effectiveQuantity === 0) {
      setCartAlert({
        type: 'error',
        message: '⚠️ Please select your ticket numbers first'
      });
      return;
    }
    
    if (!user) {
      return; // no alert for guests
    }
    
    setIsAddingToCart(true);
    setCartAlert(null);
    setAnswerError('');

    try {
      // Combined flow: any numbers picked by hand, plus any lucky-dip
      // tickets. Either or both can be non-zero.
      const manualReserved = reservedNumbers?.map(r => r?.number) || [];
      const wantsLuckyDip = (selectionMode === 'lucky-dip' || isInstantWin) && quantity > 0;

      if (manualReserved.length === 0 && !wantsLuckyDip) {
        throw new Error('No numbers selected. Please select numbers or use Lucky Dip.');
      }

      // The server checks the answer, then moves the picked numbers into the
      // basket and adds the lucky-dip tickets (random free numbers). It also
      // resets the basket's timer.
      const res = await fetch('/api/entry/add-to-basket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          competitionId: competition?.id,
          answer: questionnaireAnswers?.general_knowledge,
          numbers: manualReserved,
          luckyDip: wantsLuckyDip ? quantity : 0
        })
      });
      const data = await res.json().catch(() => ({}));
      const totalAdded = data?.added || 0;

      if (totalAdded > 0) {
        trackAddToCart(competition, totalAdded);
        window.dispatchEvent(new CustomEvent('cart:changed'));
      }

      if (data?.wrongAnswer) {
        setAnswerError(data?.error || 'That answer isn’t right. Have another look at the question.');
        setQuestionnaireCompleted(false);
        throw new Error(data?.error || 'That answer isn’t right.');
      }
      if (!res.ok || !data?.ok) {
        throw new Error(
          totalAdded > 0
            ? `${totalAdded} ticket(s) added to your basket. ${data?.error || ''}`.trim()
            : data?.error || 'Couldn’t add your tickets. Please try again.'
        );
      }

      clearPendingEntry();
      setResumed(null);

      setCartAlert({
        type: 'success',
        message: `✓ Success! ${totalAdded} ticket(s) added to your cart.`
      });

      setTimeout(() => {
        window.location.href = '/shopping-cart';
      }, 1500);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setCartAlert({
        type: 'error',
        message: error?.message || 'Couldn’t add your tickets. Please try again.'
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Manually-selected count is ALWAYS additive — even when the user
  // is on the Lucky Dip tab. So a customer can hand-pick 10 specific
  // numbers then top up with 5 lucky-dip picks and the totals (and
  // bulk discount tier) reflect 15 tickets, not just 5.
  const manualCount = (selectedNumbers?.length || 0) + (reservedNumbers?.length || 0);
  const luckyDipCount = selectionMode === 'lucky-dip' || isInstantWin ? quantity : 0;
  const effectiveQuantity = manualCount + luckyDipCount;

  // Resolve the per-ticket price via the shared bulk-pricing helper.
  // Highest tier whose minQuantity is <= effectiveQuantity wins;
  // anything below the lowest tier pays the default ticket_price.
  const basePrice = parseFloat(competition?.ticketPrice) || 0;
  const bulkTiers = competition?.bulkPricingTiers || competition?.bulk_pricing_tiers || [];
  const pricePerTicket = pricePerTicketFor({
    basePrice,
    bulkTiers,
    quantity: effectiveQuantity
  });
  const subtotal = effectiveQuantity * pricePerTicket;
  const total = subtotal;
  const savedPerTicket = Math.max(0, basePrice - pricePerTicket);
  const totalSaved = savedPerTicket * effectiveQuantity;
  const isBulkApplied = savedPerTicket > 0 && effectiveQuantity > 0;
  const normalisedTiers = visibleTiers(bulkTiers);

  // Find the next tier the user has NOT yet hit, so we can nudge them
  // ("Buy 2 more for £4 each") — gives the bulk discount real pull.
  const nextTier = normalisedTiers.find(t => t.minQuantity > effectiveQuantity);
  const ticketsToNextTier = nextTier ? Math.max(0, nextTier.minQuantity - effectiveQuantity) : 0;
  // What reaching the next tier actually costs on top of the current total.
  const nextTierPrice = nextTier ? resolveTierPrice(nextTier, basePrice) : 0;
  const costToNextTier = nextTier
    ? Math.max(0, nextTier.minQuantity * nextTierPrice - effectiveQuantity * pricePerTicket)
    : 0;
  const addLuckyDip = (n) => {
    if (selectionMode === 'lucky-dip' || isInstantWin) {
      handleQuantityChange(quantity + n);
    } else {
      setSelectionMode('lucky-dip');
      handleQuantityChange(n);
    }
  };
  // Total for a quick-pick quantity (plus any hand-picked numbers), with saving.
  const quickTotal = (q) => {
    const n = manualCount + q;
    const each = pricePerTicketFor({ basePrice, bulkTiers, quantity: n });
    return { total: each * n, saved: Math.max(0, (basePrice - each) * n) };
  };

  // Chance of winning — TWO metrics:
  //   1. "Right now" odds: user's total entries vs. the live sold pool
  //      (relevant because the prize is a guaranteed draw — the winner
  //      is picked from sold tickets even if the comp doesn't sell out)
  //   2. "If fully sold" odds: user's total entries vs. the full
  //      totalTickets pool — the floor for what their odds will be
  //      if every ticket sells
  const userOwnedAlready = userPurchasedNumbers?.length || 0;
  const userTotalEntries = userOwnedAlready + effectiveQuantity;
  const ticketsSoldNow = competition?.ticketsSold || 0;
  // (totalTickets is declared later as part of the sold-out check;
  // reuse it via competition?.totalTickets directly here.)

  // The "right now" pool is the current sold count + the tickets this
  // user is about to add (so if they go from 0 to 1 entry on an empty
  // comp it shows 1 in 1, not 1 in 0).
  const poolSizeNow = ticketsSoldNow + effectiveQuantity;
  const showChance = userTotalEntries > 0 && poolSizeNow > 0;

  const oneInNow = showChance ? Math.max(1, Math.round(poolSizeNow / userTotalEntries)) : 0;
  const percentNow = showChance ? (userTotalEntries / poolSizeNow) * 100 : 0;

  // If-fully-sold odds only meaningful when totalTickets is set and
  // the user wouldn't already exceed it.
  const compTotalTickets = competition?.totalTickets || 0;
  const fullySoldPool = Math.max(compTotalTickets, userTotalEntries);
  const showFullySold = compTotalTickets > 0 && userTotalEntries > 0 && fullySoldPool > 0;
  const oneInFullySold = showFullySold
    ? Math.max(1, Math.round(fullySoldPool / userTotalEntries))
    : 0;
  const percentFullySold = showFullySold
    ? (userTotalEntries / fullySoldPool) * 100
    : 0;
  
  // CRITICAL FIX: All three conditions MUST be true
  const canProceed = effectiveQuantity > 0 && questionnaireCompleted && isAuthenticated;

  // Logged-out visitors can choose tickets and answer the question; adding to
  // the basket needs an account, so remember their choice and bring them back.
  const isGuest = !isAuthenticated && !authLoading;
  const nextParam = encodeURIComponent(pathname || '/');
  const rememberEntry = () =>
    savePendingEntry({
      competitionId: competition?.id,
      title: competition?.title,
      url: pathname,
      mode: isInstantWin ? 'lucky-dip' : selectionMode,
      quantity: selectionMode === 'lucky-dip' || isInstantWin ? quantity : 0,
    });

  const totalTickets = competition?.totalTickets ?? 0;
  const ticketsSold = competition?.ticketsSold ?? 0;
  const isSoldOut = competition?.status === 'active' && totalTickets > 0 && ticketsSold >= totalTickets;

  // Check if competition is active (allows number selection)
  const canSelectNumbers = competition?.status === 'active';

  // If competition is not active, show disabled state message
  if (!canSelectNumbers) {
    return (
      <div className="rounded-[8px] border border-white/10 bg-[#111612] p-5 sm:p-6">
        <div className="text-center">
          <Icon name="LockClosedIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-card-foreground mb-2">
            {competition?.status === 'pending' && 'Competition Finished'}
            {competition?.status === 'drawn' && 'Draw Completed'}
            {competition?.status === 'expired' && 'Competition Expired'}
          </h3>
          <p className="text-muted-foreground text-sm">
            {competition?.status === 'pending' && 'All tickets have been sold. Watch out for the draw!'}
            {competition?.status === 'drawn' && 'The winner has been selected.'}
            {competition?.status === 'expired' && 'This competition is no longer available.'}
          </p>
        </div>
      </div>
    );
  }

  if (isSoldOut) {
    return (
      <div className="rounded-[8px] border border-white/10 bg-[#111612] p-5 sm:p-6">
        <div className="text-center">
          <Icon name="XCircleIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-card-foreground mb-2">Sold out</h3>
          <p className="text-muted-foreground text-sm">
            All tickets have been sold. Watch out for the draw announcement.
          </p>
        </div>
      </div>
    );
  }

  return (
    // A flex column so the question can sit under the heading on phones
    // (Rob) while desktop keeps it by the order summary: CSS order only.
    <div className="flex flex-col rounded-[8px] border border-white/10 bg-[#111612] p-5 sm:p-6">
      <h3 className="d text-[length:var(--t-h3)] mb-6 order-first lg:order-none">
        Get your tickets.
      </h3>
      {resumed && (
        <div className="mb-5 rounded-[6px] border-2 border-accent/40 bg-accent/10 p-3 text-sm order-[-2] lg:order-none">
          <p className="font-semibold text-foreground">Welcome back.</p>
          <p className="text-muted-foreground">
            {resumed.quantity > 0
              ? `Your ${resumed.quantity} lucky dip ticket${resumed.quantity === 1 ? ' is' : 's are'} ready. Answer the question below, then add ${resumed.quantity === 1 ? 'it' : 'them'} to your basket.`
              : 'Pick your numbers, answer the question below, then add them to your basket.'}
          </p>
        </div>
      )}
      {/* Selection Method - HIDE FOR INSTANT WIN */}
      {!isInstantWin && (
        <div className="mb-6">
          <p className="text-sm font-semibold text-foreground mb-3">How do you want to pick?</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSelectionMode('manual')}
              className={`p-4 rounded-[6px] border-2 transition-smooth ${
                selectionMode === 'manual' ? 'border-accent bg-[rgba(50,216,0,0.10)] text-foreground' : 'border-white/[0.12] bg-white/[0.03] text-muted-foreground hover:border-white/30'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon 
                  name="HandRaisedIcon" 
                  size={20} 
                  className={selectionMode === 'manual' ? 'text-green-500' : ''} 
                />
                <span className="font-bold">Pick my own</span>
              </div>
              <p className="text-xs opacity-80 text-left">
                Choose each number
              </p>
            </button>

            <button
              onClick={() => setSelectionMode('lucky-dip')}
              className={`p-4 rounded-[6px] border-2 transition-smooth ${
                selectionMode === 'lucky-dip' ? 'border-accent bg-[rgba(50,216,0,0.10)] text-foreground' : 'border-white/[0.12] bg-white/[0.03] text-muted-foreground hover:border-white/30'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="SparklesIcon" size={20} />
                <span className="font-bold">Lucky dip</span>
              </div>
              <p className="text-xs opacity-80 text-left">
                We pick at random
              </p>
            </button>
          </div>
        </div>
      )}
      
      {/* INSTANT WIN INFO BANNER */}
      {isInstantWin && (
        <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-[6px] p-4">
          <div className="flex items-start space-x-3">
            <Icon name="SparklesIcon" size={24} className="text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-bold text-yellow-800 mb-1">🎰 Instant Win Competition</h4>
              <p className="text-yellow-700 text-sm">
                Ticket numbers are automatically assigned via Lucky Dip. Some tickets win instantly when purchased!
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Quantity Selector (Lucky Dip Only OR Instant Win) */}
      {(selectionMode === 'lucky-dip' || isInstantWin) && (
        <div className="mb-6">
          <p className="text-sm font-semibold text-foreground mb-3">How many tickets?</p>

          {/* Bulk bracket boxes — one-click "buy this bracket" buttons
              so customers can jump straight to a discounted quantity. */}
          {normalisedTiers.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {/* Always-on single-ticket bracket so the default price is
                  in the same visual row as the discounted ones. */}
              <button
                type="button"
                onClick={() => handleQuantityChange(1)}
                className={`relative rounded-[6px] border-2 p-2 text-left transition-all ${
                  quantity === 1
                    ? 'border-accent bg-accent/10'
                    : 'border-border bg-card hover:border-accent/50'
                }`}
              >
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                  1 ticket
                </div>
                <div className="text-lg font-bold text-foreground">
                  {formatCurrency(basePrice)}
                </div>
              </button>
              {normalisedTiers.map((tier, idx) => {
                const tierPrice = resolveTierPrice(tier, basePrice);
                const pct = Math.round(tierDiscountPercent(tier, basePrice));
                const isActive = quantity >= tier.minQuantity &&
                  (idx === normalisedTiers.length - 1 ||
                    quantity < normalisedTiers[idx + 1]?.minQuantity);
                return (
                  <button
                    type="button"
                    key={tier.minQuantity}
                    onClick={() => handleQuantityChange(tier.minQuantity)}
                    className={`relative rounded-[6px] border-2 p-2 text-left transition-all overflow-hidden ${
                      isActive
                        ? 'border-success bg-success/10'
                        : 'border-border bg-card hover:border-success/50'
                    }`}
                  >
                    {pct > 0 && (
                      <span className="absolute top-0 right-0 bg-success text-white text-[10px] font-bold px-1.5 py-0.5 rounded-bl-md">
                        -{pct}%
                      </span>
                    )}
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                      {tier.minQuantity}+ tickets
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      {formatCurrency(tierPrice)}
                      <span className="text-[11px] font-normal text-muted-foreground ml-1">each</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mb-4">
            <input
              type="range"
              min="1"
              max="50"
              value={quantity}
              onChange={(e) => handleQuantityChange(parseInt(e?.target?.value))}
              className="w-full slider-enhanced cursor-pointer"
              style={{
                background: `linear-gradient(to right, #32d800 0%, #32d800 ${((quantity - 1) / 49) * 100}%, #444444 ${((quantity - 1) / 49) * 100}%, #444444 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span className="text-green-400 font-medium">1</span>
              <span className="text-muted-foreground">25</span>
              <span className="text-green-400 font-medium">50</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              className="w-11 h-11 rounded-[6px] bg-white/[0.07] text-foreground hover:bg-white/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <Icon name="MinusIcon" size={16} />
            </button>
            
            <div className="flex-1">
              <div className="rounded-[6px] border border-white/[0.12] bg-black/30 px-4 py-2.5 text-center">
                <span className="num text-[1.9rem] text-accent-bright">{quantity}</span>
                <span className="text-sm text-muted-foreground ml-1">
                  ticket{quantity !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            
            <button
              onClick={() => handleQuantityChange(quantity + 1)}
              disabled={quantity >= 50}
              className="w-11 h-11 rounded-[6px] bg-white/[0.07] text-foreground hover:bg-white/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <Icon name="PlusIcon" size={16} />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-4">
            {[1, 5, 10, 20]?.map((quickQuantity) => {
              const { total: qTotal, saved: qSaved } = quickTotal(quickQuantity);
              return (
                <button
                  key={quickQuantity}
                  onClick={() => handleQuantityChange(quickQuantity)}
                  className={`px-2 py-2 text-sm rounded-[6px] border transition-smooth leading-tight ${
                    quantity === quickQuantity
                      ? 'border-accent bg-accent/10 text-accent' :'border-border bg-background text-muted-foreground hover:border-accent/50 hover:text-foreground'
                  }`}
                >
                  <span className="block font-bold text-base">{quickQuantity}</span>
                  <span className="block text-xs tabular-nums">{formatCurrency(qTotal)}</span>
                  {qSaved > 0.004 && (
                    <span className="block text-[10px] font-semibold text-success tabular-nums">
                      save {formatCurrency(qSaved)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            Maximum 50 tickets per purchase • Use slider or buttons to adjust quantity
          </p>
        </div>
      )}
      
      {/* Manual Selection Summary - ONLY FOR STANDARD COMPETITIONS */}
      {selectionMode === 'manual' && !isInstantWin && (
        <div className="mb-6">
          <div className={`rounded-[6px] p-4 transition-all duration-300 ${
            showSelectionConfirmation && reservedNumbers?.length > 0
              ? 'bg-blue-50 border-2 border-blue-400 shadow-md'
              : 'bg-muted border border-border'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {showSelectionConfirmation && reservedNumbers?.length > 0 && (
                  <Icon name="ClockIcon" size={20} className="text-blue-600" />
                )}
                <span className={`text-sm font-medium ${
                  showSelectionConfirmation && reservedNumbers?.length > 0
                    ? 'text-blue-700' :'text-foreground'
                }`}>
                  {reservedNumbers?.length > 0 ? 'Reserved Numbers (Not in Cart)' : 'Selected Numbers'}
                </span>
              </div>
              <span className={`text-lg font-bold ${
                showSelectionConfirmation && reservedNumbers?.length > 0
                  ? 'text-blue-600' :'text-accent'
              }`}>
                {effectiveQuantity}
              </span>
            </div>
            {effectiveQuantity === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Choose your tickets on the{' '}
                <button
                  type="button"
                  onClick={openNumberPicker}
                  className="font-semibold text-accent underline underline-offset-4 min-h-[44px]"
                >
                  Pick your numbers
                </button>{' '}
                tab.
              </p>
            )}
            {showSelectionConfirmation && reservedNumbers?.length > 0 && (
              <div className="mt-3 flex items-start space-x-2 bg-yellow-50 p-2 rounded border border-yellow-300">
                <Icon name="ExclamationTriangleIcon" size={14} className="text-yellow-700 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-800 font-medium">
                  Important: These numbers are only RESERVED. You must answer the question and choose &ldquo;Add to basket&rdquo; to secure them.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Select Numbers Button - ONLY FOR MANUAL MODE IN STANDARD COMPETITIONS */}
      {selectionMode === 'manual' && !isInstantWin && (
        <div className="hidden lg:block mb-6">
          <button
            onClick={() => {
              if ((selectedNumbers?.length > 0 || reservedNumbers?.length > 0)) {
                const questionnaireSection = document.getElementById('questionnaire-section');
                if (questionnaireSection) {
                  questionnaireSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              } else {
                openNumberPicker();
              }
            }}
            className="btn-block btn-go w-full"
          >
            
            <span>
              {(selectedNumbers?.length > 0 || reservedNumbers?.length > 0)
                ? 'Proceed' : 'Pick your numbers'}
            </span>
          </button>
        </div>
      )}
      
      {/* Mobile CTA - ONLY FOR MANUAL MODE IN STANDARD COMPETITIONS */}
      {selectionMode === 'manual' &&
  !isInstantWin &&
  showMobileCTA &&
  !questionnaireCompleted &&
  typeof window !== 'undefined' &&
  window.innerWidth < 1024 &&
  createPortal(
    <button
      onClick={() => {
        if ((selectedNumbers?.length > 0 || reservedNumbers?.length > 0)) {
          const questionnaireSection = document.getElementById('questionnaire-section');
          if (questionnaireSection) {
            questionnaireSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          openNumberPicker();
        }
      }}
      className={`btn-block btn-go fixed left-4 right-4 shadow-2xl max-w-[calc(100vw-2rem)]
        z-[2147483647]
        ${reservedNumbers?.length > 0 
          ? (isReservationMinimised ? 'bottom-32' : 'bottom-40') 
          : 'bottom-4'
        }`}
    >
      
      <span>
        {(selectedNumbers?.length > 0 || reservedNumbers?.length > 0)
          ? 'Proceed' : 'Pick your numbers'}
      </span>
    </button>,
    document.body
  )
}
      {/* Order Summary - ENHANCED with clear status + bulk pricing */}
      <div className="rounded-[6px] bg-white/[0.04] border border-white/[0.08] p-4 mb-6">
        <h4 className="font-bold text-foreground mb-3">Your order</h4>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {effectiveQuantity} ticket{effectiveQuantity !== 1 ? 's' : ''} × {formatCurrency(pricePerTicket)}
            </span>
            <span className="text-foreground">{formatCurrency(subtotal)}</span>
          </div>

          {/* Bulk pricing applied — show original price strikethrough + savings */}
          {isBulkApplied && (
            <div className="flex items-start justify-between text-xs">
              <span className="text-muted-foreground line-through">
                Standard price {formatCurrency(basePrice)} × {effectiveQuantity}
              </span>
              <span className="text-success font-semibold">
                -{formatCurrency(totalSaved)}
              </span>
            </div>
          )}

          {/* Upsell nudge: "Add N more to drop to £X each".
              Tiers may carry either discountPercent or pricePerTicket;
              resolveTierPrice handles both, so we don't fall over to
              NaN when only a percentage is stored. */}
          {nextTier && effectiveQuantity > 0 && ticketsToNextTier > 0 &&
            ticketsToNextTier <= 10 && ticketsToNextTier <= Math.ceil(nextTier.minQuantity / 2) && (
            <div className="py-2 px-3 bg-accent/10 border border-accent/30 rounded text-xs flex items-center justify-between gap-3">
              <span>
                Add <span className="font-semibold text-accent">{ticketsToNextTier} more</span> for just{' '}
                <span className="font-semibold text-accent">{formatCurrency(costToNextTier)}</span> and all{' '}
                {nextTier.minQuantity} drop to{' '}
                <span className="font-semibold text-accent">{formatCurrency(nextTierPrice)} each</span>
              </span>
              <button
                type="button"
                onClick={() => addLuckyDip(ticketsToNextTier)}
                className="shrink-0 rounded bg-accent px-2.5 py-1.5 font-semibold text-accent-foreground hover:opacity-90"
              >
                Add {ticketsToNextTier}
              </button>
            </div>
          )}

          {/* CRITICAL STATUS INDICATOR */}
          {selectionMode === 'manual' && reservedNumbers?.length > 0 && (
            <div className="py-2 px-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
              <div className="flex items-center space-x-2">
                <Icon name="ExclamationCircleIcon" size={14} className="text-yellow-700" />
                <span className="text-xs text-yellow-800 font-semibold">
                  ⚠️ NOT IN CART - Reserved only (10 min hold)
                </span>
              </div>
            </div>
          )}

          <div className="border-t border-border pt-2 flex justify-between font-semibold">
            <span className="text-foreground">Total</span>
            <span className="text-accent text-lg">{formatCurrency(total)}</span>
          </div>
          {isBulkApplied && (
            <div className="text-xs text-success font-medium text-right">
              You saved {formatCurrency(totalSaved)} with bulk pricing
            </div>
          )}
        </div>
      </div>

      {/* Your chance of winning — two views:
          - Right now: against the live sold pool (the winner is picked
            from sold tickets, even on a guaranteed draw)
          - If fully sold: against the full totalTickets, i.e. the
            floor of how strong the odds will get if everything sells */}
      {showChance && (
        <div className="mb-6 p-4 rounded-[6px] border-2 border-accent/30 bg-gradient-to-r from-accent/10 to-accent/5">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="SparklesIcon" size={18} className="text-accent" />
            <h4 className="font-bold text-card-foreground">Your chance of winning</h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-card border border-border rounded-[6px] p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                Right now
              </div>
              <div className="text-2xl font-extrabold text-accent leading-none mt-1">
                1 in {oneInNow.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {percentNow < 1 ? percentNow.toFixed(2) : percentNow.toFixed(1)}% · against {poolSizeNow.toLocaleString()} sold
              </div>
            </div>

            {showFullySold && (
              <div className="bg-card border border-border rounded-[6px] p-3">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                  If fully sold
                </div>
                <div className="text-2xl font-extrabold text-foreground leading-none mt-1">
                  1 in {oneInFullySold.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {percentFullySold < 1 ? percentFullySold.toFixed(2) : percentFullySold.toFixed(1)}% · against {fullySoldPool.toLocaleString()} total
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-muted-foreground mt-3">
            Based on <span className="font-semibold text-foreground">{userTotalEntries}</span> ticket{userTotalEntries === 1 ? '' : 's'}
            {userOwnedAlready > 0 && (
              <> (includes {userOwnedAlready} you&rsquo;ve already bought)</>
            )}
            . The winner is drawn from sold tickets even if the competition isn&rsquo;t fully sold.
          </div>
        </div>
      )}
      {/* General Knowledge Question: under the heading on phones, here on desktop */}
      <div className="order-[-1] lg:order-none mb-6 lg:mb-0">
        <PreCartQuestionnaire
          onComplete={handleQuestionnaireComplete}
          competition={competition}
          error={answerError}
        />
      </div>
      {/* Action Buttons - UPDATED: Removed Buy Now button */}
      <div className="mt-6">
        {isGuest ? (
          <div className="rounded-[6px] border-2 border-accent/40 bg-accent/10 p-4">
            <p className="font-semibold text-foreground">
              {luckyDipCount > 0
                ? `Ready to enter with ${luckyDipCount} ticket${luckyDipCount === 1 ? '' : 's'}?`
                : 'Ready to enter?'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Create a free account or log in to add your tickets. We&apos;ll bring you straight back here with
              your tickets ready.
            </p>
            <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-2">
              <Link
                href={`/register?next=${nextParam}`}
                onClick={rememberEntry}
                className="btn-block btn-go w-full !text-[0.9rem] !px-3"
              >
                Create account
              </Link>
              <Link
                href={`/customer-login?next=${nextParam}`}
                onClick={rememberEntry}
                className="btn-block btn-line w-full !text-[0.9rem] !px-3"
              >
                Log in
              </Link>
            </div>
            {selectionMode === 'manual' && !isInstantWin && (
              <p className="mt-3 text-xs text-muted-foreground">
                You can pick your own numbers once you&apos;re logged in, or choose Lucky Dip now.
              </p>
            )}
          </div>
        ) : (
        <button
          onClick={handleAddToCart}
          disabled={!canProceed || isAddingToCart}
          className={`btn-block w-full ${
            canProceed && !isAddingToCart
              ? 'btn-go cursor-pointer' : 'bg-white/[0.06] text-muted-foreground cursor-not-allowed'
          }`}
          title={!canProceed ? 'Answer the question first' : 'Add these tickets to your basket'}
        >
          {isAddingToCart ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              <span>Adding...</span>
            </>
          ) : (
            <>
              <span>Add to basket</span>
              <Icon name="ArrowRightIcon" size={18} />
            </>
          )}
        </button>
        )}

        {/* NEW: In-App Alert - Displays below Add to Cart button */}
        {cartAlert && (
          <div 
            className={`mt-4 p-4 rounded-[6px] border-2 transition-all duration-300 ${
              cartAlert?.type === 'success' ?'bg-green-50 border-green-400' :'bg-red-50 border-red-400'
            }`}
          >
            <div className="flex items-start space-x-3">
              <Icon 
                name={cartAlert?.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} 
                size={20} 
                className={cartAlert?.type === 'success' ? 'text-green-600' : 'text-red-600'} 
              />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  cartAlert?.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {cartAlert?.message}
                </p>
              </div>
              <button
                onClick={() => setCartAlert(null)}
                className={`${
                  cartAlert?.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
                } transition-colors`}
              >
                <Icon name="XMarkIcon" size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* ENHANCED Requirements Info */}
      {/* ----------------------------------------------------------
         CHANGE #2: Hide requirement warnings for guests
         Only show this block to authenticated users.
        ----------------------------------------------------------- */}
      {(isAuthenticated && (!questionnaireCompleted || effectiveQuantity === 0)) && (
        <div className="mt-4 p-3 bg-red-500/10 border-2 border-red-500/30 rounded-[6px]">
          <div className="flex items-start space-x-2">
            <Icon name="XCircleIcon" size={16} className="text-red-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-red-700 mb-2">⚠️ Requirements Not Met:</p>
              <ul className="text-red-600 space-y-1">
                {effectiveQuantity === 0 && <li>❌ You must select ticket numbers</li>}
                {!questionnaireCompleted && (
                  <li>❌ You must answer the question</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* CRITICAL: Selection Status Reminder - ONLY FOR MANUAL MODE */}
      {selectionMode === 'manual' && !isInstantWin && reservedNumbers?.length > 0 && !questionnaireCompleted && (
        <div className="mt-4 p-4 bg-yellow-500/10 border-2 border-yellow-500/40 rounded-[8px]">
          <div className="flex items-start space-x-3">
            <Icon name="ClockIcon" size={20} className="text-yellow-700 mt-0.5" />
            <div>
              <p className="font-bold text-yellow-800 mb-1">
                🎯 Your Tickets Are On Hold (Not in Cart Yet)
              </p>
              <p className="text-yellow-700 text-sm mb-2">
                You have {reservedNumbers?.length} ticket{reservedNumbers?.length !== 1 ? 's' : ''} reserved for 10 minutes.
              </p>
              <ol className="text-yellow-700 text-sm space-y-1 list-decimal list-inside">
                <li>Answer the question</li>
                <li>Choose &ldquo;Add to basket&rdquo; to move your tickets into your basket</li>
              </ol>
            </div>
          </div>
        </div>
      )}
      {/* Payment Security */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-xs text-muted-foreground">
        <div className="flex items-center space-x-1">
          <Icon name="LockClosedIcon" size={12} />
          <span>Secure Payment</span>
        </div>
        <div className="flex items-center space-x-1">
          <Icon name="ShieldCheckIcon" size={12} />
          <span>SSL Protected</span>
        </div>
      </div>
    </div>
  );
};

TicketSelector.propTypes = {
  competition: PropTypes?.shape({
    id: PropTypes?.oneOfType([PropTypes?.string, PropTypes?.number]),
    ticketPrice: PropTypes?.number?.isRequired,
    totalTickets: PropTypes?.number,
    ticketsSold: PropTypes?.number
  })?.isRequired,
  selectedNumbers: PropTypes?.arrayOf(PropTypes?.number)?.isRequired,
  reservedNumbers: PropTypes?.arrayOf(PropTypes?.object),
  availableNumbers: PropTypes?.arrayOf(PropTypes?.number),
  isReservationMinimised: PropTypes?.bool,
  onQuantityChange: PropTypes?.func?.isRequired,
  onPromoCodeApply: PropTypes?.func?.isRequired,
  onAgeVerificationChange: PropTypes?.func?.isRequired
};

export default TicketSelector;
